import { gql, useLazyQuery } from "@apollo/client";
import {
  IIconProps,
  Label,
  PrimaryButton,
  Spinner,
  SpinnerSize,
  Stack,
} from "@fluentui/react";
import React, { useContext, useEffect, useState } from "react";
import AppContext, { IFilters } from "../../../AppContext";
import Title from "../../../components/Title";
import "./style.scss";
import { useTranslation } from "react-i18next";


export const title = "Descarga masiva de datos";
export const filters: IFilters = {
  limitedPeriodo: false,
  showMedidas: false,
  hasTicketMedio: false,
  isDesglose: false,
  showEntorno: false,
  showSegmentos: false,
  showMyData: false,
  onlyYears: true,
};

const GET_COMPARABLES_EXCEL = gql`
  query ($year: Int, $wk1: Int, $wk2: Int, $format: String) {
    getComparablesExcel(
      WeekRange: {
        Start: { Year: $year, Week: $wk1 }
        End: { Year: $year, Week: $wk2 }
      }
      Filters: { Format: $format }
    )
  }
`;

const Graph = ({ admin }: { admin?: boolean }) => {
  const{t, i18n}=useTranslation();
  const { semanas, filters } = useContext(AppContext);
  const [horizontal, setHorizontal] = useState(false);
  const [massState, setMassState] = useState<"idle" | "done" | "error">("idle");

  //Comparables Excel
  const [compState, setCompState] = useState<"idle" | "done" | "error">("idle");
  const [compLink, setCompLink] = useState<string | null>(null);
  const [compFormat, setCompFormat] = useState<"XLSX" | "CSV">("XLSX");

  const [getComparablesExcel, { loading: compLoading }] = useLazyQuery(
    GET_COMPARABLES_EXCEL,
    {
      fetchPolicy: "no-cache",
              onCompleted: (d) => {
          let raw = d?.getComparablesExcel;
          const result = typeof raw === "string" ? JSON.parse(raw) : raw;
          if (!result || result?.Error || !result?.Data) {
            setCompState("error");
            return;
          }
          // Dispara la descarga directamente aquí
          const byteArray = Uint8Array.from(atob(result.Data), c => c.charCodeAt(0));
          const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = result.Filename ?? `Comparables.xlsx`;
          a.click();
          URL.revokeObjectURL(url);
          setCompState("done");
        },
      onError: () => setCompState("error"),
    }
  );

  const GET_GRAPH = gql`
    query (
      $yr1: Int!
      $yr2: Int!
      $var: String!
      $format: String!
      $alternativo: Boolean
      $lang: String
    ) {
      graph(
        Graph: "Descarga masiva"
        WeekRange: {
          Start: { Year: $yr1, Week: 0 }
          End: { Year: $yr2, Week: 0 }
        }
        Filters: { Segmento: $var, Format: $format, Alternativo: $alternativo, Lang: $lang }
      )
    }
  `;

  const [getData, { loading }] = useLazyQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    onCompleted: (d) => {
      let raw = d?.graph;
      const result = typeof raw === "string" ? JSON.parse(raw) : raw;
      if (!result || result?.Error || !result?.Files?.length) {
        setMassState("error");
        return;
      }
      result.Files.forEach((file: { Data: string; Filename: string }, i: number) => {
        setTimeout(() => {
          const byteArray = Uint8Array.from(atob(file.Data), c => c.charCodeAt(0));
          const isCSV = file.Filename.endsWith(".csv");
          const blob = new Blob([byteArray], {
            type: isCSV
              ? "text/csv"
              : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = file.Filename;
          a.click();
          URL.revokeObjectURL(url);
        }, i * 300);
      });
      setMassState("done");
    },
    onError: () => setMassState("error"),
  });

  useEffect(() => {
    setMassState("idle");
    setCompState("idle");
    setCompLink(null);
  }, [filters, semanas]);

  const excelIcon: IIconProps = { iconName: "ExcelDocument" };
  const csvIcon: IIconProps = { iconName: "TextDocument" };
  const comparableYear = semanas?.[1]?.year ?? semanas?.[0]?.year;
  const comparablePrevYear = comparableYear ? comparableYear - 1 : "";

  const handleDetails = (_: any, checked: boolean | undefined) => {
    setHorizontal(!!checked);
  };

  return (
    <>
      <Title title={t('Data-mass-data-load')} />
      <div style={{ display: "flex", justifyContent: "center", gap: 32, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div
          className="modal-container dark in-section"
          style={{
            width: "unset",
            height: "unset",
            padding: "unset",
            background: "unset",
            position: "relative",
          }}
        >
          <div
            className="table-container"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div
              className="table"
              style={{
                padding: 40,
                margin: "0 10px",
                width: 470,
                minWidth: 280,
              }}
            >
              <div className="container" style={{ textAlign: "center" }}>
                {loading && (
                  <Spinner label={t('Generating-report')} size={SpinnerSize.large} />
                )}
                {!loading && massState === "done" && (
                  <Label style={{ paddingBottom: "2em" }}>
                    {t('Report-generated-successfully')}.
                  </Label>
                )}
                {!loading && massState === "error" && (
                  <Label style={{ paddingBottom: "2em", color: "red" }}>
                    {t('Report-not-generated')}.
                  </Label>
                )}
                {!loading && massState === "idle" && (
                  <>
                    <Label style={{ paddingBottom: "2em" }}>
                      {t("Report-generated-data", {
                        year1: semanas[1].year,
                        year2: semanas[0].year === semanas[1].year
                          ? semanas[0].year - 1
                          : semanas[0].year,
                      })}
                    </Label>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                      <div className="generate-buttons vertical-buttons">
                        <PrimaryButton
                          iconProps={excelIcon}
                          text={t('Generate-xlsx')}
                          onClick={() => {
                            getData({
                              variables: {
                                yr1: semanas[0].year,
                                yr2: semanas[1].year,
                                var: filters.segmentos[filters.variables],
                                alternativo: horizontal ?? false,
                                format: "XLSX",
                                lang: (i18n.language || 'es').slice(0, 2),
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                        <PrimaryButton
                          iconProps={csvIcon}
                          text={t('Generate-csv')}
                          onClick={() => {
                            getData({
                              variables: {
                                yr1: semanas[0].year,
                                yr2: semanas[1].year,
                                var: filters.segmentos[filters.variables],
                                alternativo: horizontal ?? false,
                                format: "CSV",
                                lang: (i18n.language || 'es').slice(0, 2),
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                      </div>
                    </Stack>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Comparables Excel */}
        <div
          className="modal-container dark in-section"
          style={{
            width: "unset",
            height: "unset",
            padding: "unset",
            background: "unset",
            position: "relative",
          }}
        >
          <div
            className="table-container"
            style={{ display: "flex", justifyContent: "center" }}
          >
            <div
              className="table"
              style={{
                padding: 40,
                margin: "0 10px",
                width: 470,
                minWidth: 280,
              }}
            >
              <div className="container" style={{ textAlign: "center" }}>
                {compLoading && (
                  <Spinner label={t("Generating-comparables")} size={SpinnerSize.large} />
                )}

                  {!compLoading && compState === "done" && (
                    <Label style={{ paddingBottom: "2em" }}>
                      {t("Comparable-generated-successfully")}
                    </Label>
                  )}

                {!compLoading && compState === "error" && (
                  <Label style={{ paddingBottom: "2em", color: "red" }}>
                    {t("Comparable-error")}
                  </Label>
                )}

                {!compLoading && compState === "idle" && (
                  <>
                    <Label style={{ paddingBottom: "2em" }}>
                      {t("Comparable-report-data", {
                        year1: comparableYear ?? "",
                        year2: comparablePrevYear,
                      })}
                    </Label>
                    <Stack horizontal tokens={{ childrenGap: 5 }}>
                      <div className="generate-buttons">
                        <PrimaryButton
                          iconProps={{ iconName: "ExcelDocument" }}
                          text={t("Generate-comparables-xlsx")}
                          disabled={!comparableYear}
                          onClick={() => {
                            setCompFormat("XLSX");
                            setCompLink(null);
                            getComparablesExcel({
                              variables: {
                                year: comparableYear,
                                wk1: 1,
                                wk2: 53,
                                format: "XLSX",
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                        <PrimaryButton
                          iconProps={{ iconName: "TextDocument" }}
                          text={t("Generate-comparables-csv")}
                          disabled={!comparableYear}
                          onClick={() => {
                            setCompFormat("CSV");
                            setCompLink(null);
                            getComparablesExcel({
                              variables: {
                                year: comparableYear,
                                wk1: 1,
                                wk2: 53,
                                format: "CSV",
                              },
                            });
                          }}
                          allowDisabledFocus
                        />
                      </div>
                    </Stack>
                  </>
                )}
              </div>
            </div>
          </div>
        </div> 
      </div> 
    </>
  );
};

export default Graph;